import { useState, useEffect } from 'react';

interface OrientationState {
  isPortrait: boolean;
  isLandscape: boolean;
  orientation: 'portrait' | 'landscape';
}

export const useOrientation = (): OrientationState => {
  const [orientation, setOrientation] = useState<OrientationState>(() => {
    // Initial state based on window dimensions
    const isPortrait = window.innerHeight > window.innerWidth;
    return {
      isPortrait,
      isLandscape: !isPortrait,
      orientation: isPortrait ? 'portrait' : 'landscape'
    };
  });

  useEffect(() => {
    const handleOrientationChange = () => {
      const isPortrait = window.innerHeight > window.innerWidth;
      const newOrientation = isPortrait ? 'portrait' : 'landscape';
      
      setOrientation({
        isPortrait,
        isLandscape: !isPortrait,
        orientation: newOrientation
      });
    };

    // Listen for both resize and orientationchange events
    window.addEventListener('resize', handleOrientationChange);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', handleOrientationChange);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  return orientation;
};

// Hook specifically for detecting orientation changes (useful for cleanup)
export const useOrientationChange = (callback: (newOrientation: 'portrait' | 'landscape', prevOrientation: 'portrait' | 'landscape') => void) => {
  const [prevOrientation, setPrevOrientation] = useState<'portrait' | 'landscape'>(() => {
    return window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';
  });

  useEffect(() => {
    const handleOrientationChange = () => {
      const isPortrait = window.innerHeight > window.innerWidth;
      const newOrientation = isPortrait ? 'portrait' : 'landscape';
      
      if (newOrientation !== prevOrientation) {
        callback(newOrientation, prevOrientation);
        setPrevOrientation(newOrientation);
      }
    };

    window.addEventListener('resize', handleOrientationChange);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', handleOrientationChange);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [callback, prevOrientation]);
};
